/**
 * Canonical credit economy schema (Firestore: system/creditEconomy).
 * Mirror logic in functions/creditEconomy.js.
 *
 * Credits are whole units (1 Credit = 1 Credit). fundsBalanceCents stores credit units
 * when creditUnitCents is 1 (legacy field name).
 * Wallet exchange uses walletCentsPerCredit (default 1 → $0.01 per Credit).
 */

export const CREDIT_ECONOMY_DOC_PATH = ['system', 'creditEconomy'];

export const APPROVAL_CATEGORY_KEYS = [
  { key: 'default', label: 'Default (fallback)' },
  { key: 'venue', label: 'New venue approval' },
  { key: 'resource', label: 'Resource submission' },
  { key: 'merch', label: 'Merch submission' },
  { key: 'accessibility', label: 'Accessibility note' },
  { key: 'environment', label: 'Environment flag' },
  { key: 'education', label: 'Education flag' },
  { key: 'spec', label: 'Spec / other venue note' },
];

export const DEFAULT_CREDIT_ECONOMY = {
  /** Stored balance increment per 1 Credit (keep at 1). */
  creditUnitCents: 1,
  /** Wallet cents added per 1 Credit on exchange (1 = $0.01). */
  walletCentsPerCredit: 1,
  /** Max Credits convertible per UTC month (0 = unlimited). */
  maxCreditsExchangePerMonth: 1100,
  defaultApprovalCreditUnits: 1,
  approvalCreditUnitsByCategory: {
    default: 1,
    venue: 1,
    resource: 1,
    merch: 1,
    accessibility: 1,
    environment: 1,
    education: 1,
    spec: 1,
  },
};

function clampInt(n, min, max, fallback) {
  const v = Math.round(Number(n));
  if (!Number.isFinite(v)) return fallback;
  return Math.min(max, Math.max(min, v));
}

/** Credits allow up to 2 decimal places (e.g. 0.5 Credits per approval). */
export function roundCredits(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function clampCredits(n, min, max, fallback) {
  const v = roundCredits(n);
  if (!Number.isFinite(Number(n))) return fallback;
  return Math.min(max, Math.max(min, v));
}

function resolveWalletCentsPerCredit(src, base) {
  if (src.walletCentsPerCredit != null) {
    return clampInt(src.walletCentsPerCredit, 0, 1_000_000, base.walletCentsPerCredit);
  }
  if (src.creditToWalletCents != null && src.creditUnitCents != null) {
    const unit = clampInt(src.creditUnitCents, 1, 1_000_000, 100);
    const rate = clampInt(src.creditToWalletCents, 0, 1_000_000, 100);
    return Math.max(0, Math.round((unit * rate) / 100));
  }
  if (src.creditToWalletCents != null) {
    return clampInt(src.creditToWalletCents, 0, 1_000_000, base.walletCentsPerCredit);
  }
  return base.walletCentsPerCredit;
}

export function normalizeCreditEconomyConfig(raw) {
  const base = DEFAULT_CREDIT_ECONOMY;
  const src = raw && typeof raw === 'object' ? raw : {};
  const byCat = { ...base.approvalCreditUnitsByCategory };
  const incoming = src.approvalCreditUnitsByCategory;
  if (incoming && typeof incoming === 'object') {
    Object.keys(byCat).forEach((key) => {
      if (incoming[key] != null) {
        byCat[key] = clampCredits(incoming[key], 0, 1_000_000, byCat[key]);
      }
    });
  }
  if (src.defaultApprovalCreditUnits != null) {
    byCat.default = clampCredits(
      src.defaultApprovalCreditUnits,
      0,
      1_000_000,
      base.defaultApprovalCreditUnits,
    );
  }
  const walletCentsPerCredit = resolveWalletCentsPerCredit(src, base);
  return {
    creditUnitCents: 1,
    walletCentsPerCredit,
    /** @deprecated written for legacy readers only */
    creditToWalletCents: walletCentsPerCredit,
    defaultApprovalCreditUnits: clampCredits(
      src.defaultApprovalCreditUnits,
      0,
      1_000_000,
      base.defaultApprovalCreditUnits,
    ),
    approvalCreditUnitsByCategory: byCat,
    maxCreditsExchangePerMonth: clampInt(
      src.maxCreditsExchangePerMonth,
      0,
      1_000_000,
      base.maxCreditsExchangePerMonth,
    ),
  };
}

export function resolveApprovalCategory({ category, submissionType } = {}) {
  const cat = String(category || '').toLowerCase().trim();
  const sub = String(submissionType || '').toLowerCase().trim();
  if (cat === 'accessibility') return 'accessibility';
  if (cat === 'merch' || sub === 'merch') return 'merch';
  if (cat === 'environment') return 'environment';
  if (cat === 'education') return 'education';
  if (cat === 'spec') return 'spec';
  if (sub === 'venue' || cat === 'venue') return 'venue';
  if (sub === 'resource' || cat === 'resource') return 'resource';
  if (cat && byCatKey(cat)) return cat;
  return 'default';
}

function byCatKey(cat) {
  return APPROVAL_CATEGORY_KEYS.some((row) => row.key === cat);
}

export function creditsBalanceToUnits(creditsBalance, creditUnitCents = 1) {
  const bal = Math.max(0, roundCredits(creditsBalance));
  return bal / Math.max(1, creditUnitCents);
}

export function formatWalletDollars(walletCents) {
  const c = Math.max(0, Math.round(Number(walletCents) || 0));
  return `$${(c / 100).toFixed(c % 100 === 0 ? 0 : 2)}`;
}

export function createCreditEconomyHelpers(config) {
  const cfg = normalizeCreditEconomyConfig(config);

  function creditEarnCentsForApproval(opts = {}) {
    const bucket = resolveApprovalCategory(opts);
    const units =
      cfg.approvalCreditUnitsByCategory[bucket] ??
      cfg.approvalCreditUnitsByCategory.default ??
      cfg.defaultApprovalCreditUnits;
    return roundCredits(Math.max(0, units) * cfg.creditUnitCents);
  }

  function creditEarnUnitsForApproval(opts = {}) {
    return creditEarnCentsForApproval(opts) / Math.max(1, cfg.creditUnitCents);
  }

  function convertCreditsCentsToWalletCents(creditsBalance) {
    const units = creditsBalanceToUnits(creditsBalance, cfg.creditUnitCents);
    return Math.floor(units * cfg.walletCentsPerCredit + 1e-9);
  }

  function creditsCentsToCreditUnits(creditsBalance) {
    return creditsBalanceToUnits(creditsBalance, cfg.creditUnitCents);
  }

  function formatCreditsAmount(creditsBalance) {
    const units = creditsCentsToCreditUnits(creditsBalance);
    const text =
      Math.abs(units - Math.round(units)) < 0.001
        ? String(Math.round(units))
        : units.toFixed(2);
    const n = Number(text);
    return `${text} ${n === 1 ? 'Credit' : 'Credits'}`;
  }

  function formatCreditToWalletRateLabel() {
    return formatWalletDollars(cfg.walletCentsPerCredit);
  }

  function formatCreditExchangeRateLine() {
    return `1 Credit → ${formatCreditToWalletRateLabel()} Wallet`;
  }

  function formatCreditsToWalletExchangeLabel(creditsBalance, walletCents) {
    return `Exchanged ${formatCreditsAmount(creditsBalance)} → ${formatWalletDollars(walletCents)} Wallet`;
  }

  /** Apply monthly exchange cap to stored credit balance (after billing reserve). */
  function capCreditsBalanceForMonthlyExchange(creditsBalanceAfterReserve) {
    const available = Math.max(0, roundCredits(creditsBalanceAfterReserve));
    const maxUnits = cfg.maxCreditsExchangePerMonth;
    if (!maxUnits || maxUnits <= 0) return available;
    const capStored = maxUnits * cfg.creditUnitCents;
    return Math.min(available, capStored);
  }

  /**
   * Max subscription invoice discount (cents) for one billing cycle:
   * maxCreditsExchangePerMonth Credits at walletCentsPerCredit, never above the plan price.
   * 0 monthly max = no Credits cap (plan price only).
   */
  function maxSubscriptionDiscountCents(planPriceCents) {
    const price = Math.max(0, Math.round(Number(planPriceCents) || 0));
    const maxUnits = cfg.maxCreditsExchangePerMonth;
    if (!maxUnits || maxUnits <= 0) return price;
    return Math.min(price, maxUnits * cfg.walletCentsPerCredit);
  }

  /** Stored Credits needed to cover an invoice discount of discountCents. */
  function creditsForDiscountCents(discountCents) {
    const rate = cfg.walletCentsPerCredit;
    if (!rate || rate <= 0) return 0;
    return Math.ceil(Math.max(0, Math.round(Number(discountCents) || 0)) / rate) * cfg.creditUnitCents;
  }

  /** Banked Credits → { discountCents, creditsUsed } for the next subscription invoice. */
  function subscriptionDiscountForCredits(creditsBalance, planPriceCents) {
    const balance = Math.max(0, roundCredits(creditsBalance));
    const rate = cfg.walletCentsPerCredit;
    if (!rate || rate <= 0 || balance <= 0) return { discountCents: 0, creditsUsed: 0 };
    const units = creditsBalanceToUnits(balance, cfg.creditUnitCents);
    const discountCents = Math.min(
      maxSubscriptionDiscountCents(planPriceCents),
      Math.floor(units * rate + 1e-9),
    );
    return {
      discountCents,
      creditsUsed: Math.min(balance, creditsForDiscountCents(discountCents)),
    };
  }

  function formatMaxCreditsExchangePerMonthLine() {
    const max = cfg.maxCreditsExchangePerMonth;
    if (!max || max <= 0) {
      return 'Monthly exchange limit: none (all spendable Credits once per UTC month)';
    }
    return `Monthly exchange limit: ${max} ${max === 1 ? 'Credit' : 'Credits'} per UTC month`;
  }

  return {
    config: cfg,
    creditUnitCents: cfg.creditUnitCents,
    walletCentsPerCredit: cfg.walletCentsPerCredit,
    /** Legacy aliases */
    creditToWalletCents: cfg.walletCentsPerCredit,
    CREDIT_EARN_CENTS: cfg.creditUnitCents,
    CREDIT_TO_WALLET_CENTS: cfg.walletCentsPerCredit,
    FUNDS_EARN_CENTS: cfg.creditUnitCents,
    creditEarnCentsForApproval,
    creditEarnUnitsForApproval,
    convertCreditsCentsToWalletCents,
    creditsCentsToCreditUnits,
    formatCreditsAmount,
    formatCreditToWalletRateLabel,
    formatCreditExchangeRateLine,
    formatCreditsToWalletExchangeLabel,
    capCreditsBalanceForMonthlyExchange,
    formatMaxCreditsExchangePerMonthLine,
    maxSubscriptionDiscountCents,
    creditsForDiscountCents,
    subscriptionDiscountForCredits,
  };
}

export const defaultCreditEconomyHelpers = createCreditEconomyHelpers(null);
